import './styles/main.css';
import { apiClient } from './lib/api/client';
import { playerManager } from './lib/player/youtube-player';
import { useStore, waitForHydration } from './lib/state/store';
import { storage } from './lib/storage/db';
import { parseSearchResults, parseAlbumTracks } from './lib/parser/youtube';
import { setupDefaultShortcuts, keyboardManager } from './utils/keyboard';
import { queueManager } from './components/QueueManager';
import {
  renderSongItem,
  renderArtistCard,
  renderAlbumCard,
  renderPlaylistCard,
  renderLoadingSkeleton,
  renderEmptyState,
  renderError,
  replaceContent,
} from './lib/ui/renderer';
import { debounce, formatTime, showToast, applyTheme } from './utils/index';
import type { Song, Album } from './types';

class ZemerApp {
  async initialize(): Promise<void> {
    console.log('🚀 Starting Zemer Music initialization...');

    // Wait for state to be rehydrated from localStorage first
    try {
      await waitForHydration();
      console.log('✅ State rehydrated from localStorage');
    } catch (error) {
      console.error('State rehydration error:', error);
    }

    // Load any additional data from IndexedDB and merge with store
    try {
      await this.syncFromIndexedDB();
      console.log('✅ Synced data from IndexedDB');
    } catch (error) {
      console.error('IndexedDB sync error:', error);
    }

    try {
      // Apply saved theme from store (after rehydration)
      const storeTheme = useStore.getState().theme;
      applyTheme(storeTheme);
      console.log('✅ Theme applied:', storeTheme);
    } catch (error) {
      console.error('Theme error:', error);
    }

    // Handle URL query parameters (from artist page navigation)
    this.handleQueryParams();

    // CRITICAL: Setup UI listeners FIRST - must always work
    try {
      this.setupUIListeners();
      console.log('✅ UI listeners attached');
    } catch (error) {
      console.error('CRITICAL: Failed to setup UI listeners:', error);
    }

    // Render initial loading states
    try {
      this.renderHome();
      this.renderArtists();
      console.log('✅ Initial render complete');
    } catch (error) {
      console.error('Render error:', error);
    }

    // Initialize player (non-blocking)
    try {
      playerManager.initialize('yt-player', {
        onReady: () => this.onPlayerReady(),
        onStateChange: (e) => this.onPlayerStateChange(e.data),
        onError: (e) => this.onPlayerError(e.data),
      }).catch((error) => {
        console.error('Failed to initialize player:', error);
        showToast('Player failed to load. Audio playback unavailable.');
      });
    } catch (error) {
      console.error('Player setup error:', error);
    }

    // Load whitelist (non-blocking)
    try {
      this.loadWhitelist();
    } catch (error) {
      console.error('Whitelist load error:', error);
    }

    // Setup keyboard shortcuts
    try {
      this.setupKeyboardShortcuts();
      console.log('✅ Keyboard shortcuts setup');
    } catch (error) {
      console.error('Keyboard shortcuts error:', error);
    }

    // Start progress updates
    try {
      this.startProgressUpdates();
    } catch (error) {
      console.error('Progress updates error:', error);
    }

    // Setup media session handlers
    try {
      this.setupMediaSession();
    } catch (error) {
      console.error('Media session error:', error);
    }

    // Subscribe to store changes (includes sync to IndexedDB)
    try {
      this.subscribeToStore();
    } catch (error) {
      console.error('Store subscription error:', error);
    }

    console.log('✅ Zemer Music initialized');
  }

  private async syncFromIndexedDB(): Promise<void> {
    const store = useStore.getState();

    // Load data from IndexedDB
    const data = await storage.syncToStore();

    // Merge favorites: prefer IndexedDB if store is empty but IndexedDB has data
    if (store.favoriteSongs.length === 0 && data.favorites.length > 0) {
      store.setFavorites(data.favorites);
      console.log(`Loaded ${data.favorites.length} favorites from IndexedDB`);
    } else if (store.favoriteSongs.length > 0 && data.favorites.length === 0) {
      // Store has data but IndexedDB doesn't - sync store to IndexedDB
      for (const song of store.favoriteSongs) {
        await storage.addFavorite(song);
      }
      console.log(`Synced ${store.favoriteSongs.length} favorites to IndexedDB`);
    }

    // Merge playlists
    if (store.playlists.length === 0 && data.playlists.length > 0) {
      store.setPlaylists(data.playlists);
      console.log(`Loaded ${data.playlists.length} playlists from IndexedDB`);
    } else if (store.playlists.length > 0 && data.playlists.length === 0) {
      for (const playlist of store.playlists) {
        await storage.createPlaylist(playlist);
      }
      console.log(`Synced ${store.playlists.length} playlists to IndexedDB`);
    }

    // Merge history into recently played if store is empty
    if (store.recentlyPlayed.length === 0 && data.history.length > 0) {
      const songs = data.history.map(h => h.song);
      store.setRecentlyPlayed(songs);
      console.log(`Loaded ${songs.length} history items into recently played`);
    }
  }

  private hideLoadingScreen(): void {
    const loadingScreen = document.getElementById('loadingScreen');
    if (loadingScreen) {
      loadingScreen.classList.add('hidden');
      // Remove from DOM after animation
      setTimeout(() => {
        loadingScreen.remove();
      }, 300);
    }
  }

  private async loadWhitelist(): Promise<void> {
    try {
      const whitelist = await apiClient.getWhitelist();
      useStore.getState().setWhitelist(whitelist);
      console.log(`✅ Loaded ${whitelist.artists.length} whitelisted artists`);

      // Re-render pages now that we have data
      this.renderHome();
      this.renderArtists();

      // Hide loading screen
      this.hideLoadingScreen();
    } catch (error) {
      console.error('Failed to load whitelist:', error);
      showToast('Failed to load whitelist. Some features may be limited.');
      // Show empty state instead of nothing
      this.renderHomeError();
      this.renderArtistsError();
      // Hide loading screen even on error
      this.hideLoadingScreen();
    }
  }

  private onPlayerReady(): void {
    console.log('✅ Player ready');
    // Apply saved volume setting
    const volume = useStore.getState().volume;
    playerManager.setVolume(volume);
  }

  private onPlayerStateChange(state: number): void {
    const isPlaying = state === 1; // YT.PlayerState.PLAYING
    const isPaused = state === 2; // YT.PlayerState.PAUSED
    const isEnded = state === 0; // YT.PlayerState.ENDED
    const isBuffering = state === 3; // YT.PlayerState.BUFFERING

    useStore.getState().setIsPlaying(isPlaying);

    // Update all play buttons with appropriate icon
    const playIcon = isPlaying
      ? '<svg viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>'
      : '<svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>';

    // Update all generic play buttons
    document.querySelectorAll('.play-btn').forEach((btn) => {
      btn.innerHTML = playIcon;
    });

    // Update specific player controls
    const playBtn = document.getElementById('playBtn');
    if (playBtn) {
      playBtn.innerHTML = playIcon;
    }

    const miniPlayBtn = document.getElementById('miniPlayBtn');
    if (miniPlayBtn) {
      miniPlayBtn.innerHTML = playIcon;
    }

    const sidebarPlayBtn = document.getElementById('sidebarPlayBtn');
    if (sidebarPlayBtn) {
      sidebarPlayBtn.innerHTML = playIcon;
    }

    // Track ended - auto-play next
    if (isEnded) {
      this.handleTrackEnded();
    }

    // Update progress bar immediately on state change
    if (isPlaying || isPaused || isBuffering) {
      this.updateProgressBar();
    }

    // Update media session
    playerManager.updatePositionState();
  }

  private updateProgressBar(): void {
    const current = playerManager.getCurrentTime();
    const duration = playerManager.getDuration();

    if (duration > 0) {
      const percent = (current / duration) * 100;

      const miniProgress = document.getElementById('miniProgress');
      const sliderFill = document.getElementById('sliderFill');
      const currentTime = document.getElementById('currentTime');
      const totalTime = document.getElementById('totalTime');

      if (miniProgress) miniProgress.style.width = `${percent}%`;
      if (sliderFill) sliderFill.style.width = `${percent}%`;
      if (currentTime) currentTime.textContent = formatTime(current);
      if (totalTime) totalTime.textContent = formatTime(duration);
    }
  }

  private onPlayerError(errorCode: number): void {
    const errorMessages: Record<number, string> = {
      2: 'Invalid video ID',
      5: 'HTML5 player error',
      100: 'Video not found',
      101: 'Video unavailable in your region',
      150: 'Video unavailable in your region',
    };

    const message = errorMessages[errorCode] || 'Playback error';
    console.error(`❌ Player error ${errorCode}: ${message}`);
    showToast(message);

    // Auto-skip after 2 seconds
    setTimeout(() => {
      const nextSong = useStore.getState().next();
      if (nextSong) {
        playerManager.loadSong(nextSong);
      } else {
        showToast('End of queue');
      }
    }, 2000);
  }

  private handleTrackEnded(): void {
    const store = useStore.getState();
    const { repeatMode, currentTrack } = store;

    // Handle repeat one - replay the same song
    if (repeatMode === 'one' && currentTrack) {
      playerManager.loadSong(currentTrack, 0);
      playerManager.play();
      return;
    }

    // Get next song (handles repeat all automatically in store.next())
    const nextSong = store.next();

    if (nextSong) {
      playerManager.loadSong(nextSong);
      playerManager.play();
      this.updateUI();
    } else {
      // No more songs - stop and reset
      useStore.getState().setIsPlaying(false);
    }
  }

  private setupKeyboardShortcuts(): void {
    setupDefaultShortcuts({
      togglePlay: () => playerManager.togglePlay(),
      next: () => {
        const nextSong = useStore.getState().next();
        if (nextSong) playerManager.loadSong(nextSong);
      },
      previous: () => {
        const prevSong = useStore.getState().previous();
        if (prevSong) playerManager.loadSong(prevSong);
      },
      volumeUp: () => {
        const store = useStore.getState();
        const newVolume = Math.min(1, store.volume + 0.1);
        store.setVolume(newVolume);
        playerManager.setVolume(newVolume);
        showToast(`Volume: ${Math.round(newVolume * 100)}%`);
      },
      volumeDown: () => {
        const store = useStore.getState();
        const newVolume = Math.max(0, store.volume - 0.1);
        store.setVolume(newVolume);
        playerManager.setVolume(newVolume);
        showToast(`Volume: ${Math.round(newVolume * 100)}%`);
      },
      toggleMute: () => {
        const store = useStore.getState();
        if (store.volume === 0) {
          const newVolume = 1;
          store.setVolume(newVolume);
          playerManager.setVolume(newVolume);
          playerManager.unMute();
          showToast('Unmuted');
        } else {
          store.setVolume(0);
          playerManager.mute();
          showToast('Muted');
        }
      },
      seekForward: () => {
        const current = playerManager.getCurrentTime();
        playerManager.seek(current + 10);
      },
      seekBackward: () => {
        const current = playerManager.getCurrentTime();
        playerManager.seek(Math.max(0, current - 10));
      },
      toggleShuffle: () => useStore.getState().toggleShuffle(),
      toggleRepeat: () => {
        const store = useStore.getState();
        const modes: Array<'off' | 'one' | 'all'> = ['off', 'all', 'one'];
        const currentIndex = modes.indexOf(store.repeatMode);
        const nextMode = modes[(currentIndex + 1) % modes.length]!;
        store.setRepeatMode(nextMode);
        showToast(`Repeat: ${nextMode}`);
      },
      showQueue: () => queueManager.toggle(),
      focusSearch: () => {
        useStore.getState().setCurrentTab(2); // Search tab
        setTimeout(() => {
          document.getElementById('searchInput')?.focus();
        }, 100);
      },
      goHome: () => useStore.getState().setCurrentTab(0),
      showHelp: () => this.showKeyboardHelp(),
    });
  }

  private setupMediaSession(): void {
    playerManager.setupMediaSessionHandlers({
      onPlay: () => {
        playerManager.play();
        this.updateProgressBar();
      },
      onPause: () => {
        playerManager.pause();
        this.updateProgressBar();
      },
      onNext: () => {
        const nextSong = useStore.getState().next();
        if (nextSong) {
          playerManager.loadSong(nextSong);
          playerManager.play();
          this.updateUI();
        }
      },
      onPrevious: () => {
        const prevSong = useStore.getState().previous();
        if (prevSong) {
          playerManager.loadSong(prevSong);
          playerManager.play();
          this.updateUI();
        }
      },
      onSeek: (time) => {
        playerManager.seek(time);
        this.updateProgressBar();
      },
      onStop: () => {
        playerManager.pause();
        useStore.getState().setIsPlaying(false);
      },
    });
  }

  private setupUIListeners(): void {
    console.log('🔧 Setting up UI listeners...');

    // Mobile menu button
    const mobileMenuBtn = document.getElementById('mobileMenuBtn');
    const sidebar = document.getElementById('sidebar');
    const mobileSidebarOverlay = document.getElementById('mobileSidebarOverlay');

    if (mobileMenuBtn && sidebar && mobileSidebarOverlay) {
      const toggleMobileSidebar = (open: boolean) => {
        if (open) {
          sidebar.classList.add('open');
          mobileSidebarOverlay.classList.add('active');
        } else {
          sidebar.classList.remove('open');
          mobileSidebarOverlay.classList.remove('active');
        }
      };

      mobileMenuBtn.addEventListener('click', () => {
        const isOpen = sidebar.classList.contains('open');
        toggleMobileSidebar(!isOpen);
        console.log('📱 Mobile menu toggled');
      });

      // Close sidebar when clicking overlay
      mobileSidebarOverlay.addEventListener('click', () => {
        toggleMobileSidebar(false);
      });
    }

    // Tab switching - sidebar navigation
    const navItems = document.querySelectorAll('.sidebar-nav .nav-item');
    console.log(`Found ${navItems.length} nav items`);

    navItems.forEach((item) => {
      item.addEventListener('click', () => {
        const pageIndex = parseInt((item as HTMLElement).dataset.page || '0', 10);
        console.log(`Tab ${pageIndex} clicked`);
        this.switchTab(pageIndex);

        // Close sidebar on mobile after navigation
        if (window.innerWidth <= 768 && sidebar && mobileSidebarOverlay) {
          sidebar.classList.remove('open');
          mobileSidebarOverlay.classList.remove('active');
        }
      });
    });

    // Search input
    const searchInput = document.getElementById('searchInput') as HTMLInputElement;
    const searchButton = document.getElementById('searchButton');

    if (searchInput) {
      const debouncedSearch = debounce(() => this.performSearch(searchInput.value), 500);
      searchInput.addEventListener('input', debouncedSearch);
      searchInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          this.performSearch(searchInput.value);
        }
      });
    }

    if (searchButton) {
      searchButton.addEventListener('click', () => this.performSearch(searchInput.value));
    }

    // Mini player
    const miniPlayer = document.getElementById('miniPlayer');
    if (miniPlayer) {
      miniPlayer.addEventListener('click', () => this.openFullPlayer());
    }

    // Play button (full player)
    const playBtn = document.getElementById('playBtn');
    if (playBtn) {
      playBtn.addEventListener('click', () => playerManager.togglePlay());
    }

    // Mini player play button
    const miniPlayBtn = document.getElementById('miniPlayBtn');
    if (miniPlayBtn) {
      miniPlayBtn.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent opening full player
        playerManager.togglePlay();
      });
    }

    // Next/Previous - Full player controls
    const nextBtn = document.getElementById('nextBtn');
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        const nextSong = useStore.getState().next();
        if (nextSong) playerManager.loadSong(nextSong);
      });
    }

    const prevBtn = document.getElementById('prevBtn');
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        const prevSong = useStore.getState().previous();
        if (prevSong) playerManager.loadSong(prevSong);
      });
    }

    // Sidebar controls
    const sidebarPlayBtn = document.getElementById('sidebarPlayBtn');
    if (sidebarPlayBtn) {
      sidebarPlayBtn.addEventListener('click', () => playerManager.togglePlay());
    }

    const sidebarNextBtn = document.getElementById('sidebarNextBtn');
    if (sidebarNextBtn) {
      sidebarNextBtn.addEventListener('click', () => {
        const nextSong = useStore.getState().next();
        if (nextSong) playerManager.loadSong(nextSong);
      });
    }

    const sidebarPrevBtn = document.getElementById('sidebarPrevBtn');
    if (sidebarPrevBtn) {
      sidebarPrevBtn.addEventListener('click', () => {
        const prevSong = useStore.getState().previous();
        if (prevSong) playerManager.loadSong(prevSong);
      });
    }

    // Seek bar
    const slider = document.getElementById('slider');
    if (slider) {
      slider.addEventListener('click', (e) => this.handleSeek(e));
    }

    // Repeat/Shuffle
    const repeatBtn = document.getElementById('repeatBtn');
    if (repeatBtn) {
      repeatBtn.addEventListener('click', () => this.cycleRepeat());
    }

    const shuffleBtn = document.getElementById('shuffleBtn');
    if (shuffleBtn) {
      shuffleBtn.addEventListener('click', () => {
        useStore.getState().toggleShuffle();
        showToast(useStore.getState().shuffleMode ? 'Shuffle on' : 'Shuffle off');
      });
    }

    // Queue button
    const queueBtn = document.getElementById('queueBtn');
    if (queueBtn) {
      queueBtn.addEventListener('click', () => queueManager.toggle());
    }

    // Close player button
    const closePlayerBtn = document.getElementById('closePlayerBtn');
    if (closePlayerBtn) {
      closePlayerBtn.addEventListener('click', () => {
        const playerSheet = document.getElementById('playerSheet');
        if (playerSheet) {
          playerSheet.classList.remove('open');
        }
      });
    }

    // Close player by clicking overlay
    const playerSheet = document.getElementById('playerSheet');
    if (playerSheet) {
      playerSheet.addEventListener('click', (e) => {
        if (e.target === playerSheet) {
          playerSheet.classList.remove('open');
        }
      });
    }
  }

  private switchTab(index: number): void {
    console.log(`🔄 Switching to tab ${index}`);

    useStore.getState().setCurrentTab(index);

    // Toggle active class on pages
    const pages = ['homePage', 'artistsPage', 'searchPage', 'libraryPage'];
    pages.forEach((pageId, i) => {
      const page = document.getElementById(pageId);
      if (page) {
        page.classList.toggle('active', i === index);
      }
    });

    document.querySelectorAll('.sidebar-nav .nav-item').forEach((item) => {
      const btnIndex = parseInt((item as HTMLElement).dataset.page || '0', 10);
      item.classList.toggle('active', btnIndex === index);
    });

    const titles = ['ZemerWeb', 'Artists', 'Search', 'Library'];
    const pageTitle = document.getElementById('pageTitle');
    if (pageTitle) {
      pageTitle.textContent = titles[index] || 'ZemerWeb';
    }

    // Lazy render pages
    if (index === 0) {
      this.renderHome(); // Re-render to show fresh random artists
    } else if (index === 3) {
      this.renderLibrary();
    }
  }

  private async performSearch(query: string): Promise<void> {
    const trimmed = query.trim();
    if (!trimmed) return;

    const searchContent = document.getElementById('searchContent');
    if (!searchContent) return;

    // Add to history
    useStore.getState().addToSearchHistory(trimmed);

    // Show loading
    replaceContent(searchContent, renderLoadingSkeleton('list', 10));

    try {
      // Ensure whitelist is loaded before searching
      let { whitelistedIds, whitelist } = useStore.getState();
      if (whitelistedIds.size === 0) {
        console.log('⏳ Whitelist not loaded yet, loading first...');
        const loadedWhitelist = await apiClient.getWhitelist();
        useStore.getState().setWhitelist(loadedWhitelist);
        whitelistedIds = useStore.getState().whitelistedIds;
        whitelist = useStore.getState().whitelist;
        console.log(`✅ Loaded ${loadedWhitelist.artists.length} whitelisted artists for search`);
      }

      // Get whitelist names for fallback matching
      const whitelistNames = whitelist?.artists.map(a => a.name.toLowerCase()) || [];

      const response = await apiClient.search(trimmed);
      console.log('🔍 Searching for:', query, 'with', whitelistedIds.size, 'whitelisted IDs and', whitelistNames.length, 'names');
      const { songs, albums } = parseSearchResults(response.results, whitelistedIds, whitelistNames);

      if (songs.length === 0 && albums.length === 0) {
        replaceContent(
          searchContent,
          renderEmptyState('🔍', 'No results', 'Try searching for whitelisted artists')
        );
        return;
      }

      searchContent.innerHTML = '';

      if (songs.length > 0) {
        const songsSection = document.createElement('div');
        songsSection.className = 'section';
        songsSection.innerHTML = `<div class="section-title">Songs (${songs.length})</div>`;

        songs.forEach((song, index) => {
          const item = renderSongItem(song, () => this.playSong(song, songs, index));
          songsSection.appendChild(item);
        });

        searchContent.appendChild(songsSection);
      }

      if (albums.length > 0) {
        const albumsSection = document.createElement('div');
        albumsSection.className = 'section';
        albumsSection.innerHTML = `<div class="section-title">Albums (${albums.length})</div>`;

        const albumsGrid = document.createElement('div');
        albumsGrid.className = 'h-scroll';

        albums.forEach((album) => {
          const card = renderAlbumCard(album, () => {
            // Navigate to dedicated album page (consistent with artist page navigation)
            const params = new URLSearchParams({
              id: album.albumId,
              title: album.title,
              artist: album.artist,
              thumb: album.thumbnail || ''
            });
            window.location.href = `/album?${params.toString()}`;
          });
          albumsGrid.appendChild(card);
        });

        albumsSection.appendChild(albumsGrid);
        searchContent.appendChild(albumsSection);
      }
    } catch (error) {
      console.error('Search failed:', error);
      replaceContent(
        searchContent,
        renderError('Search failed', () => this.performSearch(query))
      );
    }
  }

  private playSong(song: Song, queue: Song[], index: number): void {
    const store = useStore.getState();
    store.setQueue(queue, index);
    playerManager.loadSong(song);
    this.updateUI();
    storage.addToHistory(song);
  }

  private updateUI(): void {
    const store = useStore.getState();
    const song = store.currentTrack;

    if (!song) return;

    // Mini player
    const miniTitle = document.getElementById('miniTitle');
    const miniArtist = document.getElementById('miniArtist');
    const miniThumb = document.getElementById('miniThumb');
    const miniPlayer = document.getElementById('miniPlayer');

    if (miniTitle) miniTitle.textContent = song.title;
    if (miniArtist) miniArtist.textContent = song.artist;
    if (miniThumb && song.thumbnail) {
      miniThumb.innerHTML = `<img src="${song.thumbnail}" alt="">`;
    }
    if (miniPlayer) miniPlayer.classList.add('active');

    // Full player
    const trackTitle = document.getElementById('trackTitle');
    const trackArtist = document.getElementById('trackArtist');
    const artwork = document.getElementById('artwork');

    if (trackTitle) trackTitle.textContent = song.title;
    if (trackArtist) trackArtist.textContent = song.artist;
    if (artwork && song.thumbnail) {
      artwork.innerHTML = `<img src="${song.thumbnail}" alt="">`;
    }

    // Sidebar now playing
    const sidebarTitle = document.getElementById('sidebarTrackTitle');
    const sidebarArtist = document.getElementById('sidebarTrackArtist');
    const sidebarThumb = document.getElementById('sidebarTrackThumb');

    if (sidebarTitle) sidebarTitle.textContent = song.title;
    if (sidebarArtist) sidebarArtist.textContent = song.artist;
    if (sidebarThumb && song.thumbnail) {
      sidebarThumb.innerHTML = `<img src="${song.thumbnail}" alt="">`;
    }
  }

  private featuredAlbumsCache: Album[] = [];
  private featuredAlbumsLoading = false;

  private renderHome(): void {
    const homeContent = document.getElementById('homeContent');
    if (!homeContent) return;

    const store = useStore.getState();
    if (!store.whitelist) {
      // Show loading state
      homeContent.innerHTML = `
        <div class="empty">
          <div class="empty-icon">⏳</div>
          <div class="empty-title">Loading...</div>
          <div class="empty-text">Fetching music data</div>
        </div>
      `;
      return;
    }

    homeContent.innerHTML = '';

    // Recently played
    if (store.recentlyPlayed.length > 0) {
      const section = document.createElement('div');
      section.className = 'section';
      section.innerHTML = '<div class="section-title">Recently Played</div>';

      const grid = document.createElement('div');
      grid.className = 'h-scroll';

      store.recentlyPlayed.slice(0, 10).forEach((song) => {
        const item = renderSongItem(song, () =>
          this.playSong(song, store.recentlyPlayed, 0)
        );
        grid.appendChild(item);
      });

      section.appendChild(grid);
      homeContent.appendChild(section);
    }

    // Featured artists - pick 10 random artists each time
    const allArtists = [...store.whitelist.artists];
    // Shuffle array using Fisher-Yates algorithm
    for (let i = allArtists.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allArtists[i], allArtists[j]] = [allArtists[j]!, allArtists[i]!];
    }
    const featured = allArtists.slice(0, 10);

    const artistSection = document.createElement('div');
    artistSection.className = 'section';
    artistSection.innerHTML = '<div class="section-title">Featured Artists</div>';

    const artistGrid = document.createElement('div');
    artistGrid.className = 'h-scroll';

    featured.forEach((artist) => {
      // Check if we have a cached thumbnail
      const artistId = artist.id || artist.link || '';
      const cachedThumb = artistId ? apiClient.getArtistThumbnailFromCache(artistId) : undefined;
      const artistWithThumb = cachedThumb ? { ...artist, thumbnail: cachedThumb } : artist;

      const card = renderArtistCard(
        artistWithThumb,
        () => {
          // Navigate to dedicated artist page
          const url = `/artist?name=${encodeURIComponent(artist.name)}&id=${encodeURIComponent(artistId)}`;
          window.location.href = url;
        },
        { onLoadThumbnail: (id, name) => apiClient.fetchArtistThumbnail(id, name) }
      );
      artistGrid.appendChild(card);
    });

    artistSection.appendChild(artistGrid);
    homeContent.appendChild(artistSection);

    // Featured albums section
    const albumSection = document.createElement('div');
    albumSection.className = 'section';
    albumSection.id = 'featuredAlbumsSection';
    albumSection.innerHTML = '<div class="section-title">Featured Albums</div>';

    const albumGrid = document.createElement('div');
    albumGrid.className = 'h-scroll';
    albumGrid.id = 'featuredAlbumsGrid';

    // Show cached albums if available, otherwise show loading
    if (this.featuredAlbumsCache.length > 0) {
      this.renderFeaturedAlbums(albumGrid, this.featuredAlbumsCache);
    } else {
      albumGrid.innerHTML = '<div style="padding: 16px; color: var(--text-muted);">Loading albums...</div>';
      // Fetch albums in background if not already loading
      if (!this.featuredAlbumsLoading) {
        this.loadFeaturedAlbums(featured.slice(0, 5));
      }
    }

    albumSection.appendChild(albumGrid);
    homeContent.appendChild(albumSection);
  }

  private renderFeaturedAlbums(container: HTMLElement, albums: Album[]): void {
    container.innerHTML = '';
    albums.forEach((album) => {
      const card = renderAlbumCard(album, () => {
        // Navigate to dedicated album page
        const params = new URLSearchParams({
          id: album.albumId,
          title: album.title,
          artist: album.artist,
          thumb: album.thumbnail || ''
        });
        window.location.href = `/album?${params.toString()}`;
      });
      container.appendChild(card);
    });
  }

  private async loadFeaturedAlbums(artists: Array<{ name: string; id?: string; link?: string }>): Promise<void> {
    if (this.featuredAlbumsLoading) return;
    this.featuredAlbumsLoading = true;

    console.log('🎵 Loading featured albums from', artists.length, 'artists');
    const albums: Album[] = [];

    // Fetch albums from a few random artists in parallel
    const fetchPromises = artists.map(async (artist) => {
      const artistId = artist.id || artist.link || '';
      if (!artistId || !artistId.startsWith('UC')) return [];

      try {
        const response = await apiClient.browseArtist(artistId);
        const artistAlbums = this.parseArtistAlbums(response, artist.name);
        return artistAlbums.slice(0, 3); // Take up to 3 albums per artist
      } catch (e) {
        console.warn('Failed to fetch albums for', artist.name, e);
        return [];
      }
    });

    const results = await Promise.all(fetchPromises);
    results.forEach(artistAlbums => albums.push(...artistAlbums));

    // Shuffle and limit
    for (let i = albums.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [albums[i], albums[j]] = [albums[j]!, albums[i]!];
    }

    this.featuredAlbumsCache = albums.slice(0, 10);
    this.featuredAlbumsLoading = false;

    console.log('🎵 Loaded', this.featuredAlbumsCache.length, 'featured albums');

    // Update UI if grid exists
    const albumGrid = document.getElementById('featuredAlbumsGrid');
    if (albumGrid && this.featuredAlbumsCache.length > 0) {
      this.renderFeaturedAlbums(albumGrid, this.featuredAlbumsCache);
    }
  }

  private parseArtistAlbums(response: any, artistName: string): Album[] {
    const albums: Album[] = [];
    try {
      const ytData = response.results || response;
      const contents = ytData?.contents?.singleColumnBrowseResultsRenderer?.tabs?.[0]?.tabRenderer?.content
        ?.sectionListRenderer?.contents || [];

      for (const section of contents) {
        const carouselItems = section?.musicCarouselShelfRenderer?.contents || [];
        for (const item of carouselItems) {
          const cardItem = item?.musicTwoRowItemRenderer;
          if (cardItem) {
            const title = cardItem?.title?.runs?.[0]?.text || '';
            const browseId = cardItem?.navigationEndpoint?.browseEndpoint?.browseId;
            const thumbnails = cardItem?.thumbnailRenderer?.musicThumbnailRenderer?.thumbnail?.thumbnails || [];
            const thumbnail = thumbnails.length > 0 ? thumbnails[thumbnails.length - 1].url : null;

            if (browseId && browseId.startsWith('MPREb_') && title) {
              albums.push({ title, artist: artistName, albumId: browseId, thumbnail });
            }
          }
        }
      }
    } catch (e) {
      console.error('Error parsing artist albums:', e);
    }
    return albums;
  }

  private renderArtists(): void {
    const artistsContent = document.getElementById('artistsContent');
    if (!artistsContent) return;

    const store = useStore.getState();
    if (!store.whitelist) {
      // Show loading state
      artistsContent.innerHTML = `
        <div class="empty">
          <div class="empty-icon">⏳</div>
          <div class="empty-title">Loading artists...</div>
          <div class="empty-text">Fetching whitelist</div>
        </div>
      `;
      return;
    }

    artistsContent.innerHTML = `
      <div class="section-title">Whitelisted Artists (${store.whitelist.artists.length})</div>
      <div class="artists-grid"></div>
    `;

    const grid = artistsContent.querySelector('.artists-grid')!;

    store.whitelist.artists.forEach((artist) => {
      const artistId = artist.id || artist.link || '';
      const cachedThumb = artistId ? apiClient.getArtistThumbnailFromCache(artistId) : undefined;
      const artistWithThumb = cachedThumb ? { ...artist, thumbnail: cachedThumb } : artist;

      const card = renderArtistCard(
        artistWithThumb,
        () => {
          // Navigate to dedicated artist page
          const url = `/artist?name=${encodeURIComponent(artist.name)}&id=${encodeURIComponent(artistId)}`;
          window.location.href = url;
        },
        { onLoadThumbnail: (id, name) => apiClient.fetchArtistThumbnail(id, name) }
      );
      grid.appendChild(card);
    });
  }

  private renderHomeError(): void {
    const homeContent = document.getElementById('homeContent');
    if (!homeContent) return;
    homeContent.innerHTML = `
      <div class="empty">
        <div class="empty-icon">⚠️</div>
        <div class="empty-title">Connection Error</div>
        <div class="empty-text">Unable to load music data. Please check your connection and refresh.</div>
      </div>
    `;
  }

  private renderArtistsError(): void {
    const artistsContent = document.getElementById('artistsContent');
    if (!artistsContent) return;
    artistsContent.innerHTML = `
      <div class="empty">
        <div class="empty-icon">⚠️</div>
        <div class="empty-title">Connection Error</div>
        <div class="empty-text">Unable to load artist list. Please check your connection and refresh.</div>
      </div>
    `;
  }

  private toggleFavorite(song: Song): void {
    const store = useStore.getState();
    if (store.isFavorite(song.videoId)) {
      store.removeFavorite(song.videoId);
      showToast('Removed from favorites');
    } else {
      store.addFavorite(song);
      showToast('Added to favorites');
    }
  }

  private renderLibrary(): void {
    const libraryContent = document.getElementById('libraryContent');
    if (!libraryContent) return;

    const store = useStore.getState();

    libraryContent.innerHTML = '';

    // Favorites
    if (store.favoriteSongs.length > 0) {
      const section = document.createElement('div');
      section.className = 'section';
      section.innerHTML = `<div class="section-title">Favorites (${store.favoriteSongs.length})</div>`;

      const songsList = document.createElement('div');
      songsList.className = 'favorites-list';

      store.favoriteSongs.forEach((song, index) => {
        const item = renderSongItem(
          song,
          () => this.playSong(song, store.favoriteSongs, index),
          {
            showFavorite: true,
            isFavorite: true,
            onFavoriteToggle: () => this.toggleFavorite(song),
          }
        );
        songsList.appendChild(item);
      });

      section.appendChild(songsList);
      libraryContent.appendChild(section);
    }

    // Playlists
    if (store.playlists.length > 0) {
      const section = document.createElement('div');
      section.className = 'section';
      section.innerHTML = `<div class="section-title">Playlists (${store.playlists.length})</div>`;

      const grid = document.createElement('div');
      grid.className = 'h-scroll';

      store.playlists.forEach((playlist) => {
        const card = renderPlaylistCard(playlist, () => this.openPlaylist(playlist.id));
        grid.appendChild(card);
      });

      section.appendChild(grid);
      libraryContent.appendChild(section);
    }

    if (store.favoriteSongs.length === 0 && store.playlists.length === 0) {
      replaceContent(
        libraryContent,
        renderEmptyState(
          '📚',
          'Your library is empty',
          'Favorite songs and create playlists to see them here'
        )
      );
    }
  }

  // openArtist method removed - now using dedicated artist pages

  private async openAlbum(album: Album): Promise<void> {
    console.log('💿 Opening album:', album.title, album.albumId);

    const overlay = document.getElementById('artistOverlay');
    const detail = document.getElementById('artistDetail');

    if (!overlay || !detail) {
      console.error('❌ Overlay elements not found!', { overlay, detail });
      showToast('Error: Could not open album');
      return;
    }

    // Show overlay with loading state
    overlay.classList.add('open');
    detail.innerHTML = `
      <div class="artist-detail-header">
        <button class="icon-btn close-artist" onclick="document.getElementById('artistOverlay').classList.remove('open')">
          <svg viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
        </button>
        <h2>${album.title}</h2>
      </div>
      <div class="artist-detail-content">
        <div class="empty">
          <div class="empty-icon">⏳</div>
          <div class="empty-title">Loading tracks...</div>
        </div>
      </div>
    `;

    try {
      // Browse album to get tracks
      console.log('🔍 Browsing album:', album.albumId);
      const albumResponse = await apiClient.browseAlbum(album.albumId);
      console.log('📦 Raw album response:', albumResponse);

      const albumData = (albumResponse as any).results || albumResponse;
      console.log('📦 Album data for parsing:', albumData);

      const tracks = parseAlbumTracks(albumData);

      console.log('✅ Got', tracks.length, 'tracks from album');

      const contentDiv = detail.querySelector('.artist-detail-content')!;

      if (tracks.length === 0) {
        console.warn('⚠️ No tracks found in album');
        contentDiv.innerHTML = `
          <div class="empty">
            <div class="empty-icon">💿</div>
            <div class="empty-title">No tracks found</div>
            <div class="empty-text">This album appears to be empty.</div>
          </div>
        `;
        return;
      }

      contentDiv.innerHTML = '';

      // Album info section
      const infoSection = document.createElement('div');
      infoSection.className = 'section';
      infoSection.style.textAlign = 'center';
      infoSection.style.paddingBottom = '8px';

      if (album.thumbnail) {
        const artwork = document.createElement('img');
        artwork.src = album.thumbnail;
        artwork.alt = album.title;
        artwork.style.cssText = 'width: 200px; height: 200px; border-radius: 8px; margin: 0 auto 16px; display: block; box-shadow: 0 4px 12px rgba(0,0,0,0.3);';
        infoSection.appendChild(artwork);
      }

      const artistName = document.createElement('div');
      artistName.textContent = album.artist;
      artistName.style.cssText = 'opacity: 0.7; margin-bottom: 8px;';
      infoSection.appendChild(artistName);

      const trackCount = document.createElement('div');
      trackCount.textContent = `${tracks.length} tracks`;
      trackCount.style.cssText = 'opacity: 0.6; font-size: 14px;';
      infoSection.appendChild(trackCount);

      contentDiv.appendChild(infoSection);

      // Tracks section
      const tracksSection = document.createElement('div');
      tracksSection.className = 'section';
      tracksSection.innerHTML = `
        <button class="btn btn-primary" id="playAlbumBtn" style="margin-bottom: 16px; width: 100%;">
          <svg style="width: 20px; height: 20px; fill: currentColor;" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
          Play All
        </button>
        <div id="albumTracksList"></div>
      `;
      contentDiv.appendChild(tracksSection);

      const tracksList = tracksSection.querySelector('#albumTracksList')!;
      tracks.forEach((track, index) => {
        const item = renderSongItem(
          track,
          () => {
            useStore.getState().setQueue(tracks, index);
            playerManager.loadSong(track);
            playerManager.play();
          },
          { showThumbnail: false, showArtist: true }
        );
        tracksList.appendChild(item);
      });

      // Play all button handler
      const playAlbumBtn = tracksSection.querySelector('#playAlbumBtn');
      if (playAlbumBtn) {
        playAlbumBtn.addEventListener('click', () => {
          useStore.getState().setQueue(tracks, 0);
          playerManager.loadSong(tracks[0]!);
          playerManager.play();
        });
      }

    } catch (error) {
      console.error('❌ Failed to load album:', error);
      console.error('Error details:', {
        albumId: album.albumId,
        albumTitle: album.title,
        error: error instanceof Error ? error.message : String(error)
      });

      const contentDiv = detail.querySelector('.artist-detail-content')!;
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      contentDiv.innerHTML = `
        <div class="empty">
          <div class="empty-icon">⚠️</div>
          <div class="empty-title">Failed to load</div>
          <div class="empty-text">Could not fetch tracks for this album</div>
          <div class="empty-text" style="font-size: 12px; opacity: 0.6; margin-top: 8px;">Error: ${errorMsg}</div>
        </div>
      `;
    }
  }

  private openPlaylist(id: string): void {
    console.log('Opening playlist:', id);
  }

  private openFullPlayer(): void {
    const playerSheet = document.getElementById('playerSheet');
    if (playerSheet) {
      playerSheet.classList.add('open');
    }
  }

  private handleSeek(e: MouseEvent): void {
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const duration = playerManager.getDuration();
    const seekTime = duration * Math.max(0, Math.min(1, percent));
    playerManager.seek(seekTime);
    // Immediately update the progress bar
    setTimeout(() => this.updateProgressBar(), 100);
  }

  private cycleRepeat(): void {
    const store = useStore.getState();
    const modes: Array<'off' | 'one' | 'all'> = ['off', 'all', 'one'];
    const currentIndex = modes.indexOf(store.repeatMode);
    const nextMode = modes[(currentIndex + 1) % modes.length]!;
    store.setRepeatMode(nextMode);
    showToast(`Repeat: ${nextMode}`);
  }

  private startProgressUpdates(): void {
    setInterval(() => {
      // Update progress even when buffering or paused (for seek updates)
      const state = playerManager.getState();
      // PlayerState: -1=UNSTARTED, 0=ENDED, 1=PLAYING, 2=PAUSED, 3=BUFFERING, 5=CUED
      if (state === 1 || state === 2 || state === 3) {
        this.updateProgressBar();
        // Update media session position
        playerManager.updatePositionState();
      }
    }, 500); // Update more frequently for smoother progress
  }

  private subscribeToStore(): void {
    useStore.subscribe((state, prevState) => {
      // Handle track changes
      if (state.currentTrack !== prevState.currentTrack) {
        this.updateUI();
      }

      // Sync volume changes to player
      if (state.volume !== prevState.volume) {
        playerManager.setVolume(state.volume);
      }

      // Sync theme changes to DOM
      if (state.theme !== prevState.theme) {
        applyTheme(state.theme);
      }

      // Sync favorites to IndexedDB
      if (state.favoriteSongs !== prevState.favoriteSongs) {
        this.syncFavoritesToIndexedDB(state.favoriteSongs, prevState.favoriteSongs);
      }

      // Sync playlists to IndexedDB
      if (state.playlists !== prevState.playlists) {
        this.syncPlaylistsToIndexedDB(state.playlists, prevState.playlists);
      }

      // Re-render library page if favorites or playlists changed and we're on that tab
      if (state.currentTab === 3 &&
        (state.favoriteSongs !== prevState.favoriteSongs ||
          state.playlists !== prevState.playlists)) {
        this.renderLibrary();
      }
    });
  }

  private async syncFavoritesToIndexedDB(current: Song[], previous: Song[]): Promise<void> {
    // Find added favorites
    const currentIds = new Set(current.map(s => s.videoId));
    const previousIds = new Set(previous.map(s => s.videoId));

    // Songs added
    for (const song of current) {
      if (!previousIds.has(song.videoId)) {
        await storage.addFavorite(song);
      }
    }

    // Songs removed
    for (const song of previous) {
      if (!currentIds.has(song.videoId)) {
        await storage.removeFavorite(song.videoId);
      }
    }
  }

  private async syncPlaylistsToIndexedDB(current: import('./types').Playlist[], previous: import('./types').Playlist[]): Promise<void> {
    const currentIds = new Set(current.map(p => p.id));
    const previousIds = new Set(previous.map(p => p.id));

    // Playlists added or updated
    for (const playlist of current) {
      if (!previousIds.has(playlist.id)) {
        await storage.createPlaylist(playlist);
      } else {
        // Check if playlist was updated
        const prevPlaylist = previous.find(p => p.id === playlist.id);
        if (prevPlaylist && JSON.stringify(prevPlaylist) !== JSON.stringify(playlist)) {
          await storage.updatePlaylist(playlist);
        }
      }
    }

    // Playlists removed
    for (const playlist of previous) {
      if (!currentIds.has(playlist.id)) {
        await storage.deletePlaylist(playlist.id);
      }
    }
  }

  private handleQueryParams(): void {
    const fullUrl = window.location.href;
    const params = new URLSearchParams(window.location.search);
    console.log('🔍 Full URL:', fullUrl);
    console.log('🔍 Search params:', window.location.search);

    // Handle ?play= (play a queue of songs from artist page)
    let playData = params.get('play');

    // Fallback: check sessionStorage if URL params were lost (service worker issue)
    if (!playData) {
      const storedData = sessionStorage.getItem('zemer_pending_play');
      if (storedData) {
        console.log('🎵 Found pending play in sessionStorage');
        playData = storedData;
        sessionStorage.removeItem('zemer_pending_play');
      }
    }

    if (playData) {
      console.log('🎵 Found play data, length:', playData.length);
      try {
        const songs = JSON.parse(playData) as Song[];
        console.log('🎵 Parsed songs:', songs.length, songs[0]?.title);

        if (songs.length > 0) {
          // Store songs to play after player is ready
          (window as any).__pendingSongs = songs;

          // Wait for player to be ready
          const startPlayback = () => {
            console.log('🎵 Starting playback...');
            const songsToPlay = (window as any).__pendingSongs;
            if (!songsToPlay || songsToPlay.length === 0) {
              console.error('🎵 No songs to play!');
              return;
            }

            console.log('🎵 Setting queue with', songsToPlay.length, 'songs');
            useStore.getState().setQueue(songsToPlay, 0);
            console.log('🎵 Loading first song:', songsToPlay[0].title);
            playerManager.loadSong(songsToPlay[0]);
            // Auto-play with retries (browser autoplay restrictions may block first attempt)
            playerManager.play();
            // Retry play after delays in case first attempt is blocked
            setTimeout(() => playerManager.play(), 100);
            setTimeout(() => playerManager.play(), 500);
            setTimeout(() => playerManager.play(), 1000);
            this.updateUI();
            showToast(`Playing: ${songsToPlay[0].title}`);
            delete (window as any).__pendingSongs;
          };

          // Check every 200ms for player ready, up to 15 seconds
          let attempts = 0;
          const maxAttempts = 75;
          const checkReady = setInterval(() => {
            attempts++;
            console.log('🎵 Checking player ready, attempt', attempts, 'isReady:', playerManager.isReady());
            if (playerManager.isReady()) {
              console.log('🎵 Player ready after', attempts, 'attempts');
              clearInterval(checkReady);
              startPlayback();
            } else if (attempts >= maxAttempts) {
              console.error('🎵 Player never became ready after', maxAttempts, 'attempts');
              clearInterval(checkReady);
              showToast('Player failed to load');
            }
          }, 200);
        }
      } catch (e) {
        console.error('🎵 Failed to parse play data:', e);
        showToast('Failed to load songs');
      }
      // Clean URL immediately
      window.history.replaceState({}, '', '/');
      return;
    }

    // Handle ?album= (open album from artist page)
    const albumId = params.get('album');
    if (albumId) {
      console.log('💿 Opening album from URL:', albumId);
      // Wait for app to initialize, then open album
      setTimeout(() => {
        this.openAlbum({ title: 'Loading...', artist: '', albumId: decodeURIComponent(albumId), thumbnail: null });
      }, 1000);
      // Clean URL
      window.history.replaceState({}, '', '/');
      return;
    }

    console.log('🔍 No play or album params found');
  }

  private showKeyboardHelp(): void {
    const shortcuts = keyboardManager.getAll();
    const message = shortcuts
      .map((s: any) => {
        const keys = [];
        if (s.ctrl) keys.push('Ctrl');
        if (s.shift) keys.push('Shift');
        if (s.alt) keys.push('Alt');
        keys.push(s.key.toUpperCase());
        return `${keys.join('+')} - ${s.description}`;
      })
      .join('\n');

    alert(`Keyboard Shortcuts:\n\n${message}`);
  }
}

// Unregister old service workers that might be causing issues
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      console.log('🔧 Unregistering service worker:', registration.scope);
      registration.unregister();
    });
  });
}

// Initialize app when DOM is ready
console.log('📱 Main.ts loaded, readyState:', document.readyState);

let appInstance: ZemerApp;

if (document.readyState === 'loading') {
  console.log('⏳ Waiting for DOMContentLoaded...');
  document.addEventListener('DOMContentLoaded', () => {
    console.log('✅ DOMContentLoaded fired');
    appInstance = new ZemerApp();
    appInstance.initialize();
    (window as any).zemerApp = appInstance; // Export instance for debugging
  });
} else {
  console.log('✅ DOM already ready, initializing immediately');
  appInstance = new ZemerApp();
  appInstance.initialize();
  (window as any).zemerApp = appInstance; // Export instance for debugging
}
